import React, { useCallback, useEffect, useState } from 'react';
import DashboardLayout from '../dashboard/DashboardLayout';
import { LicenseQueue } from './AdminLicenses';
import ConfirmModal from '../ui/ConfirmModal';
import { apiJson } from '../../_helpers/api';

function api(path, opts) {
  return apiJson('/endpoint/admin' + path, opts);
}

const STATUS_LABEL = {
  open: 'Open', accepted: 'Accepted', confirmed: 'Confirmed', in_progress: 'In progress',
  completed: 'Completed', reported: 'Reported', paid: 'Paid', cancelled: 'Cancelled', expired: 'Expired'
};

const ROLE_TYPE = { buyer_agent: "Buyer's agent", showing_agent: 'Showing agent', both: 'Both' };

function fmtDT(d) {
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/* ─── Overview ─── */

function OverviewTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api('/overview').then(res => {
      if (res.status === 'success') setData(res);
      else setError(res.message || 'Could not load overview.');
    }).catch(() => setError('Could not load overview.'));
  }, []);

  if (error) return <div className="sh-error">{error}</div>;
  if (!data) return <div className="sh-empty">Loading…</div>;

  const s = data.showings.by_status || {};
  const tiles = [
    { label: 'Total users', value: data.users.total },
    { label: 'Active users', value: data.users.active },
    { label: 'Verified showing agents', value: data.users.verified_showing_agents },
    { label: 'Pending licenses', value: data.users.pending_licenses, warn: data.users.pending_licenses > 0 },
    { label: 'Open showings', value: s.open || 0 },
    { label: 'Posted last 7 days', value: data.showings.posted_last_7d },
    { label: 'Showings completed', value: data.showings.completed_count },
    { label: 'Completed fee volume', value: '$' + (data.showings.completed_fee_volume || 0).toLocaleString() },
    { label: 'Reviews exchanged', value: data.reviews_total }
  ];

  return (
    <>
      <div className="ld-stats" style={{ margin: '0 0 26px', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
        {tiles.map((t, i) => (
          <div className="ld-stat" key={i} style={t.warn ? { borderColor: '#fbbf24', background: 'var(--ng-accent-soft)' } : null}>
            <div className="ld-stat-num" style={{ fontSize: 28 }}>{t.value}</div>
            <div className="ld-stat-label">{t.label}</div>
          </div>
        ))}
      </div>

      <div className="sh-card">
        <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 800 }}>Showings by status</h3>
        <div className="sh-job-meta" style={{ gap: 10, flexWrap: 'wrap' }}>
          {Object.keys(STATUS_LABEL).map(k => (
            <span key={k} className={'sh-status ' + k}>{STATUS_LABEL[k]}: {s[k] || 0}</span>
          ))}
        </div>
      </div>

      {data.top_agents && data.top_agents.length ? (
        <div className="sh-card">
          <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 800 }}>Top showing agents</h3>
          <table className="db-table">
            <thead><tr><th>Agent</th><th>Showings completed</th><th>Rating</th></tr></thead>
            <tbody>
              {data.top_agents.map(a => (
                <tr key={a._id}>
                  <td>{a.name}<div className="db-td-sub">{a.email}</div></td>
                  <td>{a.showings_completed}</td>
                  <td>{a.rating ? `★ ${a.rating} (${a.ratings_count})` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

/* ─── Showings ─── */

function ShowingsTab() {
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const [error, setError] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (q) params.set('q', q);
    api('/showings?' + params.toString()).then(res => {
      if (res.status === 'success') { setData(res); setError(null); }
      else setError(res.message || 'Could not load showings.');
    }).catch(() => setError('Could not load showings.'));
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const counts = (data && data.counts) || {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  const cancel = (id, reason) => {
    api('/showings/' + id + '/cancel', { method: 'POST', body: { reason } }).then(load);
  };

  return (
    <>
      {error ? <div className="sh-error" style={{ marginBottom: 14 }}>{error}</div> : null}
      <div className="db-tabs">
        <button className={'db-tab' + (!statusFilter ? ' active' : '')} onClick={() => setStatusFilter('')}>All ({total})</button>
        {Object.keys(STATUS_LABEL).map(k => (
          <button key={k} className={'db-tab' + (statusFilter === k ? ' active' : '')} onClick={() => setStatusFilter(k)}>
            {STATUS_LABEL[k]} ({counts[k] || 0})
          </button>
        ))}
      </div>
      <div className="db-filters">
        <div className="sh-field grow">
          <label>Search</label>
          <input placeholder="Address, city, buyer, agent name/email, or showing ID"
            value={q} onChange={e => setQ(e.target.value)} />
        </div>
      </div>
      <div className="db-table-wrap">
        <table className="db-table">
          <thead><tr>
            <th>Created</th><th>Showing</th><th>Requesting agent</th><th>Showing agent</th><th>Status</th><th>Fee</th><th></th>
          </tr></thead>
          <tbody>
            {data === null ? (
              <tr><td colSpan="7" className="db-empty-row">Loading…</td></tr>
            ) : data.showings.length === 0 ? (
              <tr><td colSpan="7" className="db-empty-row">No showings match.</td></tr>
            ) : data.showings.map(s => {
              const p = s.properties[0] || {};
              const cancellable = ['completed', 'reported', 'paid', 'cancelled', 'expired'].indexOf(s.status) === -1;
              return (
                <tr key={s.request_id}>
                  <td>{fmtDT(s.created_at)}<div className="db-td-sub">#{s.request_id.slice(0, 8)}</div></td>
                  <td>{p.address}<div className="db-td-sub">{[p.city, p.state].filter(Boolean).join(', ')} · {fmtDT(s.window_start)}</div></td>
                  <td>{s.buyer_agent && s.buyer_agent.name}<div className="db-td-sub">{s.buyer_agent && s.buyer_agent.email}</div></td>
                  <td>{s.showing_agent && s.showing_agent.name ? <>{s.showing_agent.name}<div className="db-td-sub">{s.showing_agent.email}</div></> : '—'}</td>
                  <td><span className={'sh-status ' + s.status}>{STATUS_LABEL[s.status] || s.status}</span>
                    {s.cancelled && s.cancelled.reason ? <div className="db-td-sub">{s.cancelled.by}: {s.cancelled.reason}</div> : null}</td>
                  <td className="db-td-fee">${s.fee}</td>
                  <td>{cancellable ? <button className="sh-btn sh-btn-danger-ghost" style={{ padding: '6px 14px', fontSize: 12.5 }} onClick={() => setCancelling(s.request_id)}>Cancel</button> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {cancelling ? (
        <ConfirmModal
          title="Cancel this showing as support?"
          message="Both the requesting agent and the showing agent (if any) will be emailed with your reason."
          confirmLabel="Cancel showing"
          danger
          withReason
          reasonRequired
          reasonLabel="Reason (sent to both parties)"
          defaultReason="Cancelled by support."
          onConfirm={reason => { cancel(cancelling, reason); setCancelling(null); }}
          onClose={() => setCancelling(null)}
        />
      ) : null}
    </>
  );
}

/* ─── Users ─── */

function UsersTab() {
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (q) params.set('q', q);
    if (typeFilter) params.set('type', typeFilter);
    api('/users?' + params.toString()).then(res => {
      if (res && res.result) { setData(res); setError(null); }
      else setError((res && res.message) || 'Could not load users.');
    }).catch(() => setError('Could not load users.'));
  }, [q, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const update = (id, body) => {
    setBusy(id);
    api('/users/' + id + '/update', { method: 'POST', body }).then(() => { setBusy(null); load(); }).catch(() => setBusy(null));
  };

  const LIC_BADGE = { verified: 'completed', pending: 'accepted', rejected: 'cancelled', unverified: 'expired' };

  return (
    <>
      {error ? <div className="sh-error" style={{ marginBottom: 14 }}>{error}</div> : null}
      <div className="db-filters">
        <div className="sh-field grow">
          <label>Search</label>
          <input placeholder="Name, email, or phone" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <div className="sh-field">
          <label>Role type</label>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">All</option>
            <option value="buyer_agent">Buyer's agents</option>
            <option value="showing_agent">Showing agents</option>
            <option value="both">Both</option>
          </select>
        </div>
      </div>
      <div className="db-table-wrap">
        <table className="db-table">
          <thead><tr>
            <th>User</th><th>Type</th><th>Status</th><th>License</th><th>Stats</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {data === null ? (
              <tr><td colSpan="6" className="db-empty-row">Loading…</td></tr>
            ) : data.result.length === 0 ? (
              <tr><td colSpan="6" className="db-empty-row">No users match.</td></tr>
            ) : data.result.map(u => (
              <tr key={u._id}>
                <td>{u.name || '(no name)'}{u.role === 'admin' ? <span className="sh-status open" style={{ marginLeft: 8 }}>Admin</span> : null}
                  <div className="db-td-sub">{u.email}{u.phone_number ? ' · ' + u.phone_number : ''}</div></td>
                <td>
                  <select value={u.type || ''} disabled={busy === u._id}
                    onChange={e => update(u._id, { type: e.target.value })}>
                    <option value="buyer_agent">Buyer's agent</option>
                    <option value="showing_agent">Showing agent</option>
                    <option value="both">Both</option>
                  </select>
                </td>
                <td>
                  <select value={u.status || ''} disabled={busy === u._id}
                    onChange={e => update(u._id, { status: e.target.value })}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="profile_not_complete">Profile incomplete</option>
                    <option value="deleted">Deleted</option>
                  </select>
                </td>
                <td>
                  <span className={'sh-status ' + (LIC_BADGE[u.license_status] || 'expired')}>{u.license_status || 'unverified'}</span>
                  {u.license && u.license.number ? <div className="db-td-sub">{u.license.number} ({u.license.state})</div> : null}
                </td>
                <td>
                  {u.showings_completed ? <>✓ {u.showings_completed}</> : '—'}
                  {u.rating ? <div className="db-td-sub">★ {u.rating} ({u.ratings_count})</div> : null}
                </td>
                <td>
                  <button className="sh-btn sh-btn-outline" style={{ padding: '6px 14px', fontSize: 12.5 }}
                    disabled={busy === u._id}
                    onClick={() => update(u._id, { role: u.role === 'admin' ? 'user' : 'admin' })}>
                    {u.role === 'admin' ? 'Remove admin' : 'Make admin'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data && data.total > data.result.length ? (
        <p className="sh-page-sub" style={{ marginTop: 10 }}>Showing {data.result.length} of {data.total} — refine the search to narrow down.</p>
      ) : null}
    </>
  );
}

/* ─── Reports ─── */

const REPORT_STATUS_LABEL = { pending: 'Pending', reviewed: 'Reviewed', resolved: 'Resolved', dismissed: 'Dismissed' };

function ReportsTab() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    api('/reports?' + params.toString()).then(res => {
      if (res.status === 'success') { setData(res); setError(null); }
      else setError(res.message || 'Could not load reports.');
    }).catch(() => setError('Could not load reports.'));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setReportStatus = (id, status, note) => {
    setBusy(id);
    api('/reports/' + id + '/status', { method: 'POST', body: { status, note } })
      .then(res => {
        setBusy(null);
        if (res.status === 'success') load();
        else setError(res.message || 'Could not update report.');
      })
      .catch(() => { setBusy(null); setError('Network error.'); });
  };

  return (
    <>
      {error ? <div className="sh-error" style={{ marginBottom: 14 }}>{error}</div> : null}
      <div className="db-tabs">
        {Object.keys(REPORT_STATUS_LABEL).map(k => (
          <button key={k} className={'db-tab' + (statusFilter === k ? ' active' : '')} onClick={() => setStatusFilter(k)}>
            {REPORT_STATUS_LABEL[k]}
          </button>
        ))}
        <button className={'db-tab' + (!statusFilter ? ' active' : '')} onClick={() => setStatusFilter('')}>All</button>
      </div>
      <div className="db-table-wrap">
        <table className="db-table">
          <thead><tr>
            <th>Filed</th><th>Type</th><th>Showing</th><th>Reporter</th><th>Message</th><th>Status</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {data === null ? (
              <tr><td colSpan="7" className="db-empty-row">Loading…</td></tr>
            ) : data.reports.length === 0 ? (
              <tr><td colSpan="7" className="db-empty-row">No reports match.</td></tr>
            ) : data.reports.map(r => (
              <tr key={r._id}>
                <td>{fmtDT(r.created_at)}</td>
                <td>{r.type || '—'}</td>
                <td>{r.target_id ? <span className="db-td-sub">#{String(r.target_id).slice(0, 8)}</span> : '—'}</td>
                <td>{(r.reporter && r.reporter.name) || '—'}<div className="db-td-sub">{(r.reporter && r.reporter.email) || ''}</div></td>
                <td style={{ maxWidth: 260, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.message || r.reason || '—'}</td>
                <td><span className={'sh-status ' + (r.status === 'pending' ? 'accepted' : r.status === 'resolved' ? 'completed' : 'expired')}>{REPORT_STATUS_LABEL[r.status] || r.status}</span>
                  {r.resolution_note ? <div className="db-td-sub">{r.resolution_note}</div> : null}</td>
                <td>
                  {r.status === 'pending' ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="sh-btn sh-btn-outline" style={{ padding: '6px 12px', fontSize: 12.5 }}
                        disabled={busy === r._id} onClick={() => setReportStatus(r._id, 'reviewed')}>Mark reviewed</button>
                      <button className="sh-btn sh-btn-primary" style={{ padding: '6px 12px', fontSize: 12.5 }}
                        disabled={busy === r._id} onClick={() => setReportStatus(r._id, 'resolved')}>Resolve</button>
                      <button className="sh-btn sh-btn-danger-ghost" style={{ padding: '6px 12px', fontSize: 12.5 }}
                        disabled={busy === r._id} onClick={() => setReportStatus(r._id, 'dismissed')}>Dismiss</button>
                    </div>
                  ) : r.status === 'reviewed' ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="sh-btn sh-btn-primary" style={{ padding: '6px 12px', fontSize: 12.5 }}
                        disabled={busy === r._id} onClick={() => setReportStatus(r._id, 'resolved')}>Resolve</button>
                      <button className="sh-btn sh-btn-danger-ghost" style={{ padding: '6px 12px', fontSize: 12.5 }}
                        disabled={busy === r._id} onClick={() => setReportStatus(r._id, 'dismissed')}>Dismiss</button>
                    </div>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ─── Shell ─── */

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'showings', label: 'Showings' },
  { key: 'users', label: 'Users' },
  { key: 'licenses', label: 'License queue' },
  { key: 'reports', label: 'Reports' }
];

export default function AdminDashboard({ location }) {
  const params = new URLSearchParams(location ? location.search : '');
  const [tab, setTab] = useState(params.get('tab') || 'overview');

  return (
    <DashboardLayout>
      <h1>Admin console</h1>
      <div className="sh-tabs" style={{ marginBottom: 22 }}>
        {TABS.map(t => (
          <button key={t.key} className={'sh-tab' + (tab === t.key ? ' active' : '')} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'overview' ? <OverviewTab /> : null}
      {tab === 'showings' ? <ShowingsTab /> : null}
      {tab === 'users' ? <UsersTab /> : null}
      {tab === 'licenses' ? <LicenseQueue /> : null}
      {tab === 'reports' ? <ReportsTab /> : null}
    </DashboardLayout>
  );
}
